import { useForm } from '@tanstack/react-form';
import { createContext, useContext } from 'react';
import { useSettings } from '../../../context/settings-context';
import {
  type PriceListBuilderState,
  type PriceListBuilderValidated,
  priceListBuilderSchema,
  priceListEditBuilderSchema,
} from '../schemas';

type PriceListFormProps<TError = unknown> = {
  initialState?: PriceListBuilderState;
  isEditing?: boolean;
  onSubmit?: (payload: PriceListBuilderValidated) => Promise<void> | void;
  onFailValidation?: () => Promise<void> | void;
  onSubmitError?: (error: TError) => Promise<void> | void;
};

export function usePriceListFormLogic<TError = unknown>({
  initialState,
  isEditing,
  onSubmit,
  onFailValidation: onSubmitInvalid,
  onSubmitError: onError,
}: PriceListFormProps<TError>) {
  const settings = useSettings();
  const schema = isEditing ? priceListEditBuilderSchema : priceListBuilderSchema;

  return useForm({
    defaultValues:
      initialState ??
      ({
        name: '',
        handle: '',
        currency: settings.baseCurrency,
        automaticPricing: undefined,
        manualPricing: undefined,
      } satisfies PriceListBuilderState),
    validators: {
      onSubmitAsync: schema,
    },
    onSubmit: async ({ value }) => {
      const payload = await schema.parseAsync(value);
      try {
        await onSubmit?.(payload);
      } catch (err) {
        await onError?.(err as TError);
        // throw so the useFormProps can catch it to check if the submission was successful
        throw err;
      }
    },
    onSubmitInvalid,
  });
}

type PriceListFormApi = ReturnType<typeof usePriceListFormLogic>;

export const PriceListFormContext = createContext<PriceListFormApi | null>(null);

export function usePriceListForm() {
  const context = useContext(PriceListFormContext);

  if (!context) {
    throw new Error('usePriceListForm must be used within a PriceListFormContext.Provider');
  }

  return context;
}
